/**
 * Date formats for My Calendar's column headers.
 *
 * DHTMLX sizes a scale cell by dividing the container, never by what the label
 * needs, and it neither wraps nor ellipsises — a label that does not fit spills
 * over its neighbour, or is clipped mid-word when the text is centred. So the
 * format has to be chosen for the width, which is what this module is.
 *
 * Measured at 390px against this app's own stylesheet: a week column is 45px
 * while "Mon, September 14" needs 123px — every one of the seven clipped — and
 * a month column is 52px while "Wednesday" needs 74px.
 *
 * `scrollWidth` is NOT a usable check for these cells: the label is centred, so
 * it is clipped equally on both sides and scrollWidth stays equal to
 * clientWidth while the text is visibly cut. Width claims here come from a
 * text-width probe instead.
 *
 * This is the same fix as dental-clinic-app/src/utils/schedulerScale.js, minus
 * the toolbar parts: that app's calendar keeps DHTMLX's own navline with three
 * view tabs in it, which had to be restacked; this one drives the scheduler
 * from its own React toolbar, so there is nothing to restack here.
 *
 * Formats are DHTMLX date_to_str patterns:
 *   %D short day name ("Mon")        %l full day name ("Monday")
 *   %F month name ("September")      %j day of month, no leading zero
 */

/** The shared phone breakpoint (hooks/useIsMobile.js), as a number. */
export const MOBILE_MAX = 767;

/**
 * Below this, a week column is 40px or less and "Mon 14" cannot fit at any
 * readable size — 41px is needed at 12px, and 38px even at 11px. Measured:
 * fits at 375px (42px column) and above, fails at 360px by a single pixel.
 */
export const NARROW_MAX = 374;

export const SCALE_FORMATS = {
  // DHTMLX's own defaults, restated so the desktop path is explicit rather than
  // "whatever the library happens to do" — and so an upgrade that moves them
  // fails a test instead of quietly changing the calendar.
  desktop: { week: '%D, %F %j', month: '%l' },
  // "Mon 14" / "Mon". The week scale keeps the date number: without it a
  // dentist cannot tell which day of the month a column is.
  mobile: { week: '%D %j', month: '%D' },
  // On the narrowest phones the number is what has to go.
  narrow: { week: '%D', month: '%D' },
};

export function scaleTier(width) {
  if (width > MOBILE_MAX) return 'desktop';
  return width <= NARROW_MAX ? 'narrow' : 'mobile';
}

export default function scaleFormats(width) {
  return SCALE_FORMATS[scaleTier(width)];
}
